// Import Dependencies
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { reaction } from 'mobx';

import { ghToken } from '~/common/globals.js';
import { PATH_RUBY_TERMS_AND_CONDITIONS_PAGE, LMI_LOGIN_FLOW_ENABLED } from '~/common/utils/consts';
import removeURLParameter from '~/common/utils/removeUrlParam.js';
import { FLOWS_ENUM } from '~/common/stores/ui/loginStore';
import { gtm, viewContentEvent, startBuyFlowEvent, loginEvent, userInformationEvent } from '~/common/utils/tracking';

// Import Components
import HeaderGroup from '~/common/components/HeaderGroup';
import Subheader from '~/common/components/Subheader';
import CommonButton from '~/common/components/CommonButton';
import Centering from '~/common/components/Centering';
import Select from '~/common/components/Select';
import Banner from '~/common/components/Banner';

import rubyIlo from '~/common/images/ruby-screens-illo.png';

import styles from './ruby.css';

@withRouter
@inject('modalStore', 'loginStore', 'rubyStore', 'formsStore')
@observer
export default class Ruby extends Component {
    componentDidMount() {
        const { history, loginStore, rubyStore } = this.props;
        const { pathname, search } = history.location;

        // update page description
        document.querySelector('meta[name="description"]').content = 'Ruby & Grasshopper | Get a Live Receptionist for Your Business';

        // if auth token in the url
        if (ghToken()) {
            // save token
            loginStore.setAuthToken(ghToken());
            // "refresh" the page, while removing the convert param
            history.push({
                pathname,
                search: removeURLParameter(search, 'convert')
            });
            // if auth token is not already saved
        } else if (!loginStore.authToken.length > 0) {
            // set option so login modal logs the user via username
            loginStore.setLoginByUsername(true);
            loginStore.setLogInFlow(FLOWS_ENUM.RUBY);
            // set up and open login modal
            this.observeLoginModal();
        }

        rubyStore.fetchRubyPlans();
        gtm(viewContentEvent('upgrade flow', 'checkout', 'upgrade flow'));
        reaction(
            () => this.props.rubyStore.fetchedPlans,
            () => this.autoUpgradeDetails()
        );
    }

    // Once Ruby Upgrade Details are available call the gtm events
    autoUpgradeDetails = () => {
        reaction(
            () => this.props.loginStore.userInfo,
            () => {
                if (this.props.rubyStore.fetchedPlans) {
                    gtm(loginEvent());
                    gtm(userInformationEvent());
                    gtm(startBuyFlowEvent());
                }
            }
        );
    };

    observeLoginModal = () => {
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        if (modalFeatureType) {
            this.triggerLoginModal(modalFeatureType);
        }
    };

    triggerLoginModal = (modalFeatureType) => {
        const { modalStore } = this.props;
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    };

    componentWillUnmount() {
        document.querySelector('meta[name="description"]').content = 'Signup instantly for Grasshopper, starting at just $31 a month. Pick a phone number and a plan that work for your business today!';
    }

    selectPlan = () => this.props.formsStore.RubyPlanValidation();

    // hydrate states select input
    setRubyPlans = () => {
        const { rubyStore } = this.props;

        if (!rubyStore.fetchedPlans) return <option key="plans-loading" value="">Loading</option>;

        return rubyStore.rubyPlans.map((plan, i) => {
            return (
                <option
                    key={i}
                    value={plan.ChargeID}
                >{plan.Name}</option>
            );
        });
    };

    confirmPlan = () => {
        const { formsStore, rubyStore } = this.props;

        formsStore.validateForm(['RubyPlan'], true);

        if (!(formsStore.doesFormHaveErrors(['RubyPlan']))) rubyStore.submitRubyRequest();
    };

    renderFormStep = () => {
        const { formsStore, rubyStore, loginStore } = this.props;
        const { RubyPlan, RubyPlanError } = formsStore;
        const { userInfo } = loginStore;
        const { FirstName, LastName, Email } = userInfo;
        const { canAddRuby, fetchedPlans } = rubyStore;
        return (
            <Fragment>
                {
                    loginStore.userInfo &&
                    <Fragment>
                        <Subheader>Customer Information</Subheader>
                        {(FirstName && LastName) && <p>{`${FirstName} ${LastName}`}</p>}
                        {loginStore.userFirstNumber && <p>{loginStore.userFirstNumber}</p>}
                        {Email && <p>{Email}</p>}
                    </Fragment>
                }

                <Subheader modifier={styles.subheader}>Ruby Plan</Subheader>
                <Select
                    disabled={!fetchedPlans || !canAddRuby}
                    error={RubyPlanError}
                    id="ruby-plans"
                    label="Select a Plan"
                    name="RubyPlan"
                    onChange={this.selectPlan}
                    selected={RubyPlan}
                    value={RubyPlan}
                >
                    {this.setRubyPlans()}
                </Select>
                <Subheader modifier={styles.subheader}>Payment Method</Subheader>
                <p>Your Ruby service will be added to your Grasshopper bill.</p>
                <CommonButton
                    disabled={!canAddRuby}
                    modifier={styles.submit}
                    onClick={this.confirmPlan}
                >Confirm my order</CommonButton>
                <small>
                    By clicking “Confirm my order” you are agreeing to the
                    <a
                        className={styles.terms}
                        href={PATH_RUBY_TERMS_AND_CONDITIONS_PAGE}
                        target="_blank"
                    > Terms & Conditions
                    </a>
                    , and to receiving communications from Ruby.
                </small>
            </Fragment>
        );
    };

    renderSecondStep = () => {
        return (
            <Fragment>
                <Subheader>
                    Schedule an orientation with a Ruby specialist.
                </Subheader>
                <p>
                    A member of our Onboarding team is looking forward to
                    getting your account up and running at your scheduled time.
                    If you have not yet scheduled an onboarding appointment,
                    please reach out to your Ruby sales rep by calling <a href="tel:8666117829"> 866-611-7829</a>.
                </p>
                <p>
                    Once your account is ready, you’ll receive an activation
                    email that includes your unique Ruby telephone number and
                    other pertinent account information. The moment you receive
                    that email, we are ready to answer your calls (so don’t
                    forget to forward your line).
                </p>
                <p>
                    Please don’t hesitate to reach out at <a href="mailto:hello@ruby.com">hello@ruby.com</a> if
                    there’s anything we can help with in the meantime.
                </p>
            </Fragment>
        );
    };

    renderBannerCopy() {
        const { errorSubmittingRequest, errorRequestIsAlreadySubmitted,
            errorRequestUserIsNotAdmin, errorAlreadyRubyUser,
            errorAccountIsDunning, errorAccountIsToBeCancelled,
            errorAccountIsTrial } = this.props.rubyStore;

        if (errorAlreadyRubyUser) {
            return <p> You already have an active Ruby subscription! Please contact Grasshopper Care if you are attempting a plan change.</p>;
        }

        if (errorSubmittingRequest) {
            return <p><b>Something went wrong!</b> Please select a plan and click “Confirm my order” to complete your Ruby purchase.</p>;
        }

        if (errorRequestIsAlreadySubmitted) {
            return <p>Your Ruby account is currently pending. If you have any questions, please call the Ruby team at <a href="tel:8442799405">844-279-9405.</a></p>;
        }

        if (errorRequestUserIsNotAdmin) {
            return <p>Uh oh, something went wrong. Please confirm that you have administrator permissions in Grasshopper before adding Ruby.</p>;
        }

        if (errorAccountIsDunning) {
            return <p> Uh oh, something went wrong. Please check the credit card on file in your Grasshopper account and ensure it is up to date.</p>;
        }

        if (errorAccountIsToBeCancelled) {
            return <p> Uh oh, something went wrong. Please confirm that your Grasshopper account meets the active requirements for adding Ruby. </p>;
        }

        if (errorAccountIsTrial) {
            return <p><b>Something went wrong!</b> Please upgrade to a paid version of Grasshopper to complete your Ruby purchase.</p>;
        }

        return <p><b>Almost done!</b> Please select a plan and click “Confirm my order” to complete your Ruby purchase.</p>;
    }

    render() {
        const { rubyStore, formsStore } = this.props;
        const { userConfirmedOption, isErrorState } = rubyStore;

        return (
            <Centering>
                <HeaderGroup modifiers={styles.header}>
                    <h1>{userConfirmedOption ? 'Welcome to Ruby!' : 'Add Ruby to your Grasshopper account.'}</h1>
                    <h2>{userConfirmedOption ? 'We want your first impression to be as unique as your business.' : 'Start turning callers into customers!'}</h2>
                </HeaderGroup>
                {
                    !userConfirmedOption &&
                        <Banner errorState={isErrorState} modifiers={styles['banner-area']}>
                            {this.renderBannerCopy()}
                        </Banner>
                }

                <section className={styles.row}>
                    <div className={styles.column}>
                        <img
                            alt="Ruby Product Illusrtation"
                            className={styles.img}
                            src={rubyIlo}
                        />
                    </div>
                    <div className={styles.column}>
                        {userConfirmedOption ?
                            this.renderSecondStep()
                            :
                            this.renderFormStep()
                        }
                    </div>
                </section>
                {
                    !userConfirmedOption &&
                    <p className={styles.disclaimer}>
                        {formsStore.RubyPlan ?
                            `* Minutes in excess of your plan will be billed at $${rubyStore.getPerMinuteCharges}/minute.`
                            :
                            '* Minutes in excess of your plan will be billed at the per minute rate.'
                        }
                    </p>
                }

            </Centering>
        );
    }
}
